import type { UsageEntry } from "./entry-kind"

export type UsageOutcome =
  | "ok"
  | "client_error"
  | "server_error"
  | "network_error"
  | "paid"
  | "cancelled"
  | "failed"
  | "pending"

export type UsageChatType = "private" | "group" | "supergroup" | "channel" | "sender"

interface UsageEventFields {
  event: "app_open" | "page_view" | "tab_view" | "page_time" | "mutation" | "invoice_closed"
  route: string
  ref_seq?: number
  duration_ms?: number
  tab?: string
  name?: string
  entry?: UsageEntry
  outcome?: UsageOutcome
  chat_type?: UsageChatType
  chat_id?: number
  ad_source?: string
}

export interface UsageEventPayload extends UsageEventFields {
  seq: number
  age_ms: number
}

export interface UsageBatch {
  session_id: string
  platform: string
  events: UsageEventPayload[]
}

interface QueuedEvent {
  sessionId: string
  at: number
  fields: UsageEventFields & { seq: number }
}

export interface UsageAppOpen {
  entry: UsageEntry
  name?: string
  tab?: string
  chatType?: UsageChatType
  chatId: number | null
  adSource?: string
}

export interface UsageTrackerDeps {
  now: () => number
  isEnabled: () => boolean
  newSessionId: () => string
  platform: () => string
  /** Resolves once the request settles; failures are dropped, never retried. */
  send: (batch: UsageBatch, keepalive: boolean) => Promise<void>
}

const FLUSH_SIZE = 20
const FLUSH_INTERVAL_MS = 30_000
const MAX_QUEUE = 200
const MAX_BATCH = 50
const MAX_SEQ = 100_000
const MAX_AGE_MS = 600_000
const MAX_SPAN_MS = 1_800_000
export const RESUME_AFTER_MS = 30 * 60_000

const TAB_PATTERN = /^[a-z][a-z0-9_-]{0,39}$/
const NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,79}$/

export const coerceTab = (tab: string): string => (TAB_PATTERN.test(tab) ? tab : "other")
export const coerceName = (name: string): string => (NAME_PATTERN.test(name) ? name : "other")

const groupChatId = (chatId: number | null): number | undefined =>
  chatId !== null && Number.isSafeInteger(chatId) && chatId < 0 ? chatId : undefined

interface PageVisit {
  route: string
  refSeq: number
  visibleSince: number | null
}

export const createUsageTracker = (deps: UsageTrackerDeps) => {
  let started = false
  let sessionId = ""
  let seq = 0
  let queue: QueuedEvent[] = []
  let timer: ReturnType<typeof setTimeout> | null = null
  let inFlight = false
  let location = { route: "/", tab: "" }
  let page: PageVisit | null = null
  let chatId: number | null = null
  let visible = true
  let hiddenAt: number | null = null

  const clearTimer = () => {
    if (timer === null) return
    clearTimeout(timer)
    timer = null
  }

  const scheduleFlush = () => {
    if (timer !== null || queue.length === 0) return
    timer = setTimeout(() => {
      timer = null
      flush(false)
    }, FLUSH_INTERVAL_MS)
  }

  const takeBatch = (): UsageBatch | null => {
    const now = deps.now()
    queue = queue.filter(event => now - event.at <= MAX_AGE_MS)
    if (queue.length === 0) return null
    const first = queue[0]

    let size = 0
    while (size < queue.length && size < MAX_BATCH && queue[size].sessionId === first.sessionId) size++
    const taken = queue.slice(0, size)
    queue = queue.slice(size)

    return {
      session_id: first.sessionId,
      platform: deps.platform(),
      events: taken.map(({ at, fields }) => ({ ...fields, age_ms: Math.max(0, now - at) }))
    }
  }

  const flush = (keepalive: boolean) => {
    clearTimer()
    if (keepalive) {
      for (let batch = takeBatch(); batch; batch = takeBatch()) void deps.send(batch, true)
      return
    }
    if (inFlight) return

    const batch = takeBatch()
    if (!batch) return
    inFlight = true
    void deps.send(batch, false).finally(() => {
      inFlight = false
      if (queue.length >= FLUSH_SIZE) flush(false)
      else scheduleFlush()
    })
  }

  const enqueue = (fields: UsageEventFields): number => {
    if (!started || !deps.isEnabled() || seq >= MAX_SEQ) return 0
    seq += 1
    queue.push({ sessionId, at: deps.now(), fields: { ...fields, seq } })
    if (queue.length > MAX_QUEUE) queue.shift()
    if (queue.length >= FLUSH_SIZE) flush(false)
    else scheduleFlush()
    return seq
  }

  const closeSpan = () => {
    if (!page || page.visibleSince === null) return
    const duration = Math.min(deps.now() - page.visibleSince, MAX_SPAN_MS)
    page.visibleSince = null
    if (duration > 0) {
      enqueue({ event: "page_time", route: page.route, ref_seq: page.refSeq, duration_ms: duration })
    }
  }

  const openPage = () => {
    const refSeq = enqueue({
      event: "page_view",
      route: location.route,
      tab: location.tab ? coerceTab(location.tab) : undefined,
      chat_id: groupChatId(chatId)
    })
    page = { route: location.route, refSeq, visibleSince: visible ? deps.now() : null }
  }

  const beginSession = (open: Omit<UsageAppOpen, "chatId">) => {
    sessionId = deps.newSessionId()
    seq = 0
    enqueue({
      event: "app_open",
      route: location.route,
      entry: open.entry,
      name: open.name ? coerceName(open.name) : undefined,
      tab: open.tab ? coerceTab(open.tab) : undefined,
      chat_type: open.chatType,
      chat_id: groupChatId(chatId),
      ad_source: open.adSource
    })
    openPage()
  }

  return {
    start: (open: UsageAppOpen) => {
      if (started) return
      started = true
      chatId = open.chatId
      beginSession(open)
    },

    /** `tab` labels the page_view only; switches on the same route are reported by the Tabs component through trackTab. */
    setLocation: (route: string, tab: string | null) => {
      const previousRoute = location.route
      location = { route, tab: tab ?? "" }
      if (!started || route === previousRoute) return
      closeSpan()
      openPage()
    },

    trackTab: (tab: string) => {
      if (!started || !page || !tab) return
      enqueue({ event: "tab_view", route: page.route, tab: coerceTab(tab) })
    },

    trackMutation: (name: string, outcome: UsageOutcome) => {
      enqueue({ event: "mutation", route: location.route, name: coerceName(name), outcome })
    },

    trackInvoiceClosed: (outcome: UsageOutcome) => {
      enqueue({ event: "invoice_closed", route: location.route, outcome })
    },

    setChatId: (nextChatId: number | null) => {
      chatId = nextChatId
    },

    hide: () => {
      if (!visible) return
      visible = false
      hiddenAt = deps.now()
      closeSpan()
      flush(true)
    },

    show: () => {
      if (visible) return
      visible = true
      const hiddenFor = hiddenAt === null ? 0 : deps.now() - hiddenAt
      hiddenAt = null
      if (!started) return
      if (hiddenFor >= RESUME_AFTER_MS) {
        beginSession({ entry: "resume" })
        return
      }
      if (page) page.visibleSince = deps.now()
    }
  }
}

export type UsageTracker = ReturnType<typeof createUsageTracker>
